/**
 * Thin wrapper around BroadcastChannel for syncing auth state across tabs,
 * with a storage-event fallback for environments without BroadcastChannel.
 */
export type BroadcastMessage =
  | { type: "logout"; reason?: "user" | "sessionExpired" | "idle" }
  | { type: "tokensUpdated" }
  | { type: "loginSuccess" };

export class BroadcastBus {
  private channel: BroadcastChannel | null = null;
  private readonly storageKey: string;
  private storageListener?: (e: StorageEvent) => void;

  constructor(private readonly channelName: string) {
    this.storageKey = `${channelName}:__bus__`;
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      this.channel = new BroadcastChannel(channelName);
    }
  }

  subscribe(handler: (message: BroadcastMessage) => void): () => void {
    if (this.channel) {
      const listener = (e: MessageEvent<BroadcastMessage>) => handler(e.data);
      this.channel.addEventListener("message", listener);
      return () => this.channel?.removeEventListener("message", listener);
    }

    // Fallback: `storage` events fire in *other* tabs when localStorage changes.
    if (typeof window !== "undefined") {
      this.storageListener = (e: StorageEvent) => {
        if (e.key !== this.storageKey || !e.newValue) return;
        try {
          handler(JSON.parse(e.newValue) as BroadcastMessage);
        } catch {
          /* ignore malformed payloads */
        }
      };
      window.addEventListener("storage", this.storageListener);
      return () => {
        if (this.storageListener) window.removeEventListener("storage", this.storageListener);
      };
    }

    return () => {};
  }

  post(message: BroadcastMessage): void {
    if (this.channel) {
      this.channel.postMessage(message);
      return;
    }
    if (typeof window !== "undefined") {
      // Writing triggers a `storage` event in *other* tabs only.
      window.localStorage.setItem(this.storageKey, JSON.stringify({ ...message, _t: Date.now() }));
    }
  }

  close(): void {
    this.channel?.close();
    if (this.storageListener && typeof window !== "undefined") {
      window.removeEventListener("storage", this.storageListener);
    }
  }
}
